import { CustomTab } from "@/components/UI/Tabs";
import { useGetCustomerOrderByIdQuery } from "@/services/api/user/customerApi";
import { formatCurrency, formatDate, getStatusColor } from "@/utils/helper";
import {
  Avatar,
  Badge,
  Button,
  Modal,
  Row,
  Col,
  Tag,
  Typography,
  Rate,
} from "antd";
import { useEffect, useState } from "react";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CopyOutlined,
  FireOutlined,
  ShoppingOutlined,
  DollarOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { PromoSVG } from "@/asset/icons";
// import PromoOfferModal from "./PromoOfferModal"; // This import is no longer needed here

const { Title, Text } = Typography;

const CustomerDetailsModal = ({
  open,
  onClose,
  customerData,
  handleOpenOfferModal, // Receive this prop from parent
}) => {
  // Use customerData.id for the query and skip if customerData.id is not available
  const { data: customerOrders } = useGetCustomerOrderByIdQuery(
    customerData?.id,
    { skip: !customerData?.id }
  );
  const [activeTab, setActiveTab] = useState(0);
  const [showSatisfied, setShowSatisfied] = useState(false);

  const orders = customerOrders?.orders || [];
  const customerInfo = customerOrders?.user || {};

  useEffect(() => {
    // Reset activeTab when customerData changes to default to the first tab
    if (customerData) {
      setActiveTab(0);
    }
  }, [customerData]);

  useEffect(() => {
    setShowSatisfied(customerInfo?.average_user_rating >= 4);
  }, [customerInfo?.average_user_rating]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };

  const renderOrdersColumn = () => {
    const customerDetailsTabs = [
      { label: "Order History" },
      { label: "Customer Review" },
      { label: "Loyalty Program" },
    ];

    return (
      <div>
        <div className="flex items-center gap-2 mb-10">
          <Avatar
            size={56}
            src={customerData?.image_url || undefined}
            style={{
              backgroundColor: "#E8C300",
              fontSize: "20px",
              fontWeight: 600,
            }}
          >
            {!customerData?.image_url &&
              customerData?.full_name
                .split(" ")
                .map((name) => name[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {customerData?.full_name}
              </h3>
              {/* Assuming rank needs to be displayed here, adjust if customerData.rank is not suitable */}
              <Badge count={customerData?.rank} />
            </div>
            <div className="text-sm font-semibold text-gray-400">
              Customer ID #{customerData?.id}
            </div>
          </div>
        </div>

        {/* Tabs for Customer Details */}
        <CustomTab
          tabs={customerDetailsTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className="mb-0 rounded-md"
        />

        <div className="mt-6">
          {activeTab === 0 && (
            <div>
              {/* Order History Content */}
              {orders?.length === 0 ? (
                <Text>No orders found for this customer.</Text>
              ) : (
                orders?.map((order) => {
                  const { color, backgroundColor } = getStatusColor(
                    order.status.name
                  );

                  return (
                    <div
                      key={order.id}
                      className="mb-4 rounded-lg border border-gray-200 bg-white"
                    >
                      {/* Order Header */}
                      <div className="flex justify-between items-start bg-[#F9F9FA] border-b border-gray-200 p-4">
                        <div className="flex gap-8">
                          <div>
                            <div className="text-[#A0A4A8] text-xs mb-1">
                              Order placed
                            </div>
                            <div className="font-semibold text-sm">
                              {formatDate(order.start_date)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[#A0A4A8] text-xs mb-1">
                              Total
                            </div>
                            <div className="font-semibold text-sm">
                              {formatCurrency(order.total_amount)}
                            </div>
                          </div>
                          <div>
                            <div className="text-[#A0A4A8] text-xs mb-1">
                              {order.location ? "Pickup from" : "Delivered to"}
                            </div>
                            <div className="font-semibold text-sm">
                              {order.location
                                ? order.location.name
                                : order.user?.address || "N/A"}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-400 text-xs mb-2">
                            Order #{order.reference}
                          </div>
                          <Tag
                            style={{
                              color,
                              backgroundColor,
                              border: `1px solid ${color}20`,
                              borderRadius: "8px",
                              padding: "2px 8px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            {order.status.name}
                          </Tag>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div className="p-4">
                        <div className="font-semibold border-l-4 pl-2 border-[#F9A022] text-sm mb-3 text-gray-700">
                          Item:{" "}
                          {order.items
                            ?.map((item) => item.productable?.name)
                            .filter(Boolean)
                            .join(", ")}
                        </div>

                        {/* Product Images */}
                        <div className="flex gap-2 mb-4">
                          {order.items?.map((item, idx) => (
                            <img
                              key={idx}
                              src={
                                item.productable?.image?.url ||
                                item.productable?.image
                              }
                              alt={item.productable?.name || "Product"}
                              className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                            />
                          ))}
                        </div>

                        {/* Order Timeline */}
                        <div className="flex flex-col items-end ">
                          <div className=" w-[50%] pt-2 ">
                            <div className="flex justify-between items-center">
                              {/* Start Date */}
                              <div className="text-xs font-medium mb-1">
                                {formatDate(order.start_date)}
                              </div>

                              {/* End Date */}

                              <div className="text-xs font-medium mb-1">
                                {order.end_date
                                  ? formatDate(order.end_date)
                                  : formatDate(order.estimated_end_date)}
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              {/* Start Date */}

                              <div className="w-3 h-3 rounded-full bg-orange-500 relative z-10"></div>

                              {/* Progress Line */}
                              <div className="flex-1 mx-4  my-auto relative">
                                <div className="h-0.5 bg-gray-300 w-full"></div>
                                <div
                                  className="h-0.5 absolute top-0 left-0"
                                  style={{
                                    width:
                                      order.status.name === "Delivered"
                                        ? "100%"
                                        : "50%",
                                    backgroundColor:
                                      order.status.name === "Delivered" ||
                                      order.status.name === "pickedUp"
                                        ? "#10B981"
                                        : "#F59E0B",
                                  }}
                                ></div>
                              </div>

                              {/* End Date */}

                              <div
                                className="w-3 h-3 rounded-full relative z-10"
                                style={{
                                  backgroundColor:
                                    order.status.name === "Delivered" ||
                                    order.status.name === "pickedUp"
                                      ? "#10B981"
                                      : "#F59E0B",
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 1 && (
            <div>
              {/* Customer Reviews Content */}
              {showSatisfied && (
                <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-6 mb-6 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Satisfied Customer
                    </h3>
                    <p className="text-sm text-gray-600">
                      {customerData?.full_name} is very satisfied with the
                      service.
                    </p>
                  </div>
                  <div className="text-6xl">😊</div>
                </div>
              )}

              {/* Rating Summary */}
              <div className="mb-6">
                <div className="flex items-center gap-8">
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Rating</div>
                    <div className="flex  gap-2">
                      <span className="text-4xl font-bold">
                        {customerInfo.average_user_rating}
                      </span>
                      <div>
                        <Rate
                          disabled
                          defaultValue={customerInfo.average_user_rating}
                          className="text-sm my-auto"
                        />
                        <div className="text-xs text-gray-500">
                          {customerInfo?.total_rating_count}reviews
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-16">
                          5 - Excellent
                        </span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gray-800 h-2 rounded-full"
                            style={{ width: "60%" }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600 w-4">6</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-16">
                          4 - Good
                        </span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gray-800 h-2 rounded-full"
                            style={{ width: "50%" }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600 w-4">5</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-16">
                          3 - Okay
                        </span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gray-800 h-2 rounded-full"
                            style={{ width: "20%" }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600 w-4">2</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Review Card */}
              <div className="border border-gray-200 rounded-lg p-4 max-h-[40vh] overflow-auto">
                {orders?.length === 0 ? (
                  <Text>No orders found for this customer.</Text>
                ) : (
                  orders?.map((order) => (
                    <div key={order.id} className="mb-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          Order-{""}
                          {order.reference}
                        </span>
                        {/* <CopyOutlined className="text-gray-400 text-xs cursor-pointer" /> */}
                      </div>

                      {order.items?.map((item) => (
                        <div key={item.id} className="my-3 py-2 border-b-2">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex gap-2">
                              {" "}
                              <img
                                src={item.productable?.image.url}
                                alt={item.productable?.name}
                                className="w-12 h-12 object-cover rounded-lg"
                              />
                              <div className="text-sm text-gray-600 ">
                                Item: {item.productable?.name}
                              </div>
                            </div>

                            <Rate
                              disabled
                              defaultValue={item.productable?.average_rating}
                              className="text-sm"
                            />
                          </div>
                          {item.productable?.ratings?.map((rating) => (
                            <>
                              <div className="text-sm mb-3">
                                {rating.comment}
                              </div>
                            </>
                          ))}
                          <div className="flex gap-4 text-xs text-gray-500">
                            <span>
                              Order Date: {formatDate(order.start_date)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 2 && (
            <div>
              {/* Loyalty Program Content */}
              <div className="bg-white rounded-lg border border-gray-200 p-8">
                <Title level={5} className="text-orange-600 font-semibold mb-6">
                  Coupons and Discount
                </Title>

                <div className="flex flex-col items-center justify-center py-12">
                  <PromoSVG />

                  <div className="text-center text-gray-700 mb-6">
                    Offer this customer a promo discount
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    // Call the handleOpenOfferModal prop from the parent
                    onClick={handleOpenOfferModal}
                    style={{
                      backgroundColor: "#D97706",
                      borderColor: "#D97706",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Offer Promo
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };
  const renderCustomerInfoColumn = () => {
    return (
      <div className="p-6">
        {/* Send Message Button */}
        {/* <div className="flex justify-end mb-6">
          <Button icon={<MailOutlined />} className="flex items-center gap-2">
            Send Message
          </Button>
        </div> */}

        {/* Customer Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <FireOutlined className="text-orange-500" />
              <span className="text-xs text-gray-600">Popular Order</span>
            </div>
            <div className="font-semibold">
              {customerInfo?.popular_order ||
                customerData?.most_bought ||
                "N/A"}
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarOutlined className="text-orange-500" />
              <span className="text-xs text-gray-600">Order Value</span>
            </div>
            <div className="font-semibold">
              {formatCurrency(
                customerInfo?.order_value || customerData?.total_spent || "N/A"
              )}
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingOutlined className="text-orange-500" />
              <span className="text-xs text-gray-600">Total Order</span>
            </div>
            <div className="font-semibold">
              {customerInfo?.total_orders || customerData?.total_order || "N/A"}
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <ClockCircleOutlined className="text-orange-500" />
              <span className="text-xs text-gray-600">Last Order</span>
            </div>
            <div className="font-semibold">
              {formatDate(customerInfo?.last_order_date) || "N/A"}
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-3">
            <UserOutlined className="text-gray-400" />
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1">Customer ID</div>
              <div className="font-semibold">
                {customerInfo.id || customerData?.id}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <MailOutlined className="text-gray-400" />
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1">Email Address</div>
              <div className="font-semibold">
                {customerInfo?.email || customerData?.email}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <UserOutlined className="text-gray-400" />
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1">First Name</div>
              <div className="font-semibold">
                {customerInfo?.first_name || customerData?.first_name}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <UserOutlined className="text-gray-400" />
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1">Last Name</div>
              <div className="font-semibold">
                {customerInfo?.last_name || customerData?.last_name}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <PhoneOutlined className="text-gray-400" />
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1">Phone Number</div>
              <div className="font-semibold">
                {customerInfo?.phone || customerData?.phone_number}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <CalendarOutlined className="text-gray-400" />
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1">Date Created</div>
              <div className="font-semibold">
                {formatDate(
                  customerInfo?.created_at || customerData?.date_created
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title={
          <div className="space-y-1">
            <Title level={4} className="m-0">
              Customer Details
            </Title>
            <Text className="text-sm text-gray-400 m-0">
              View information about your customer below
            </Text>
          </div>
        }
        maskClosable
        destroyOnClose
        width="90vw"
        style={{ top: 10, maxWidth: 1400 }}
        styles={{
          body: { maxHeight: "calc(100vh - 190px)", overflowY: "auto" },
        }}
        footer={
          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        }
      >
        <Row className="p-4">
          {/* Left Column Content */}
          <Col
            xs={24}
            md={24}
            lg={12}
            className="p-6 lg:border-r border-gray-200"
          >
            {renderOrdersColumn()}
          </Col>
          <Col xs={24} md={24} lg={12}>
            {renderCustomerInfoColumn()}
          </Col>
        </Row>
      </Modal>
      {/* PromoOfferModal is now rendered in CustomersManager, so remove it here */}
    </>
  );
};

export default CustomerDetailsModal;
